import {
  getPMICalculation,
  getPrincipleInterest,
  getPropertyInsurance,
  getRemainingInterest,
} from '@elevationscu/common-mortgage-calculator';
import {
  calculateEstimatedMonthlyPayment,
  fixNaN,
  hasSecondLoanOnly,
  isDifferenceGreaterThan30Days,
  roundUpToNearestEighth,
} from '../utils/Operations';

export class LoanAssumptions {
  constructor(data = {}) {
    this.estimatedMortageBalance = data?.estimatedMortageBalance ?? '';
    this.estimatedInterestRate = fixNaN(roundUpToNearestEighth(parseFloat(data?.estimatedInterestRate) || 0));
    this.estimatedMonthlyPayment = data?.estimatedMonthlyPayment ?? 0;
    this.estimatedSecondMortgageBalance = data?.estimatedSecondMortgageBalance ?? 0;
    this.estimatedSecondInterestRate = fixNaN((parseFloat(data?.estimatedSecondInterestRate) || 0));
    this.estimatedSecondMonthlyPayment = data?.estimatedSecondMonthlyPayment ?? 0;
    this.estimatedSecondCurrentLoanAmount = data?.estimatedSecondCurrentLoanAmount ?? 0;
    this.estimatedSecondCurrentLoanType = data?.estimatedSecondCurrentLoanType ?? '';
    this.estimatedHomeValue = data?.estimatedHomeValue ?? 0;
    this.estimatedRemainingInterest = data?.estimatedRemainingInterest ?? 0;
    this.occupancyType = data?.occupancyType ?? 'Primary residence';
    this.estimatedRemainingMonths = data?.estimatedRemainingMonths ?? 0;
    this.propertyType = data?.propertyType ?? '';
    this.estimatedPrincipalAndInterest = data?.estimatedPrincipalAndInterest ?? 0;
    this.estimatedTaxes = parseFloat(data?.estimatedTaxes) || 0;
    this.estimatedMortgageInsurance = parseFloat(data?.estimatedMortgageInsurance) || 0;
    this.includeSecondLoan = data?.includeSecondLoan || 'No';
    this.estimatedPropertyInsurance = parseFloat(data?.estimatedPropertyInsurance) || 0;
    this.estimatedFloodInsurance = parseFloat(data?.estimatedFloodInsurance) || 0;
    this.originalLoanVal = data?.originalLoanVal || 0;
    this.loanTerm = data?.loanTerm || 0;
    this.secondLoanTerm = data?.secondLoanTerm || 0;
  }

  // This static method should only be used when we get data from VALUE CHECK
  static setFromValueCheck(data = {}, updatedFicoInfo = {}) {
    const isSecondLoanOnly = hasSecondLoanOnly(data?.loans);
    const monthlyTaxes = ((data.taxDetails?.taxDueAmt || null) ?? 0) / 12;

    let loanTermTmp = 0;
    let secondLoanTermTmp = 0;
    let roundedInterestRatePercentage = 0;
    let interestRate = 0;
    let currentMortgageBalance = 0;
    let principalInterest = 0;
    let pmiCalculation = 0;
    let pmi = 0;
    let remMonths = 0;
    let propertyInsurance = 0;
    let monthlyPayment = 0;
    let remainingInterest = 0;
    let secondMortgageBalance = null;
    let secondInterestRate = null;
    let secondCurrentLoanAmount = null;
    let secondCurrentLoanType = '';

    if (isSecondLoanOnly) {
      secondMortgageBalance = data.loans[0]?.balance;
      secondInterestRate = (data.loans[0]?.rate ?? 0) * 100;
      secondCurrentLoanAmount = data.loans[0]?.originalBalance;
      secondCurrentLoanType = data.loans[0]?.type;
      secondLoanTermTmp = data.loans[0]?.term != null ? data.loans[0].term * 12 : 0;
    } else {
      loanTermTmp = data.loans?.[0]?.term != null ? data.loans[0].term * 12 : 0;

      roundedInterestRatePercentage = fixNaN(roundUpToNearestEighth((data.loans?.[0]?.rate || 0) * 100));
      interestRate = roundedInterestRatePercentage / 100;
      currentMortgageBalance = data.loans?.[0]?.balance || 0;
      principalInterest = fixNaN(getPrincipleInterest(interestRate, loanTermTmp, updatedFicoInfo.originalBalance, currentMortgageBalance));
      pmiCalculation = getPMICalculation(updatedFicoInfo);
      pmi = (!isDifferenceGreaterThan30Days(updatedFicoInfo?.saleDate, updatedFicoInfo?.docDate) && (typeof pmiCalculation === 'number')) ? Math.ceil(pmiCalculation) : 0;
      propertyInsurance = fixNaN(parseFloat(getPropertyInsurance(data.marketingData?.value, data.characteristics?.propertyType)));
      monthlyPayment = fixNaN(calculateEstimatedMonthlyPayment(principalInterest, monthlyTaxes, propertyInsurance, 0, pmi));
      const remainingComputed = getRemainingInterest(currentMortgageBalance, interestRate, principalInterest);
      remMonths = remainingComputed.remainingMonths;
      remainingInterest = remainingComputed.remainingInterest;
    }

    if (data.loans?.length > 1) {
      secondMortgageBalance = data.loans[1]?.balance;
      secondInterestRate = (data.loans[1]?.rate ?? 0) * 100;
      secondCurrentLoanAmount = data.loans[1]?.originalBalance;
      secondCurrentLoanType = data.loans[1]?.type;
      secondLoanTermTmp = data.loans[1]?.term != null ? data.loans[1].term * 12 : 0;
    }

    const loan = {
      estimatedMortageBalance: currentMortgageBalance,
      estimatedInterestRate: roundedInterestRatePercentage,
      estimatedMonthlyPayment: monthlyPayment,
      estimatedSecondMortgageBalance: secondMortgageBalance,
      estimatedSecondInterestRate: secondInterestRate,
      estimatedSecondMonthlyPayment: '0',
      estimatedSecondCurrentLoanAmount: secondCurrentLoanAmount,
      estimatedSecondCurrentLoanType: secondCurrentLoanType,
      estimatedHomeValue: data.marketingData?.value ?? null,
      estimatedRemainingInterest: remainingInterest,
      occupancyType: data.ownerOccupied === true ? 'Primary residence' : 'Investment property',
      estimatedRemainingMonths: remMonths,
      propertyType: data.characteristics?.propertyType,
      estimatedPrincipalAndInterest: principalInterest,
      estimatedTaxes: monthlyTaxes,
      estimatedMortgageInsurance: pmi,
      includeSecondLoan: 'No',
      estimatedPropertyInsurance: propertyInsurance,
      originalLoanVal: updatedFicoInfo.originalBalance,
      loanTerm: loanTermTmp,
      secondLoanTerm: secondLoanTermTmp,
    };

    return new LoanAssumptions(loan);
  }
}
